import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styles from './Login.module.css';
import { HOST } from '../config/api';
import { getStoredShareInvite } from '../lib/shareInvite';

const EmailVerification = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [message, setMessage] = useState('We’re verifying your email now.');
    const [isSuccess, setIsSuccess] = useState(false);
    const token = searchParams.get('token');
    const hasFetched = useRef(false);
    const shareInvite = useMemo(() => getStoredShareInvite(), []);

    useEffect(() => {
        if (!token || hasFetched.current) {
            if (!token) {
                setMessage('This verification link is incomplete or missing. Please open the link from your email again.');
                setIsSuccess(false);
            }
            return;
        }

        hasFetched.current = true;
        const verifyEmail = async () => {
            try {
                const response = await fetch(HOST + `/v1/verify_email?token=${encodeURIComponent(token)}`, {
                    method: 'GET',
                });

                if (response.ok) {
                    setMessage('Your email has been verified. You can now log in and continue.');
                    setIsSuccess(true);
                    setTimeout(() => {
                        navigate('/login', { state: shareInvite ? { shareInvite } : undefined });
                    }, 3000);
                } else {
                    setMessage('Email verification failed. The link may be invalid or expired. Please register again or request a new verification email.');
                    setIsSuccess(false);
                }
            } catch (error) {
                setMessage('Something went wrong while verifying your email. Please try again in a moment.');
                setIsSuccess(false);
                console.error('Error during email verification:', error);
            }
        };

        verifyEmail();
    }, [token, navigate, shareInvite]);

    return (
        <div className={styles.authPage}>
            <section className={styles.authShell}>
                <div className={styles.authIntro}>
                    <div className={styles.authImageWrap}>
                        <img src="/black_and_white_image.png" alt="Underwater training" className={styles.authImage} />
                        <div className={styles.authImageOverlay}>
                            <span>Lead the Way</span>
                            <strong>Stay close to the next step.</strong>
                            <p>Verification should feel quick, clear, and connected to the rest of your onboarding flow.</p>
                        </div>
                    </div>

                    <div className={styles.authCopy}>
                        <span className={styles.authEyebrow}>Email verification</span>
                        <h1>Confirm your account and keep moving.</h1>
                        <p>Once your email is verified, you can log in and continue into the right workspace for your role.</p>
                    </div>
                </div>

                <div className={styles.authPanel}>
                    <div className={styles.successPanel}>
                        <span className={styles.panelKicker}>Verification</span>
                        <h2>{isSuccess ? 'Email verified' : 'Verification issue'}</h2>
                        <p className={isSuccess ? styles.registrationMessageSuccess : styles.registrationMessageFailure}>{message}</p>
                        {isSuccess ? (
                            <p className={styles.registrationMessageSuccess}>
                                {shareInvite
                                    ? 'Redirecting to login shortly. If you verified on this browser, we’ll continue with the invite after login.'
                                    : 'Redirecting to login shortly. You can also continue immediately.'}
                            </p>
                        ) : null}
                        <Link className={styles.primaryButton} to="/login" state={shareInvite ? { shareInvite } : undefined}>
                            Go to login
                        </Link>
                        {!isSuccess ? (
                            <Link className={styles.secondaryButton} to="/register" state={shareInvite ? { shareInvite } : undefined}>
                                Create account again
                            </Link>
                        ) : null}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default EmailVerification;
